import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'

// Illustration by Esma Melike Sezer on Unsplash.
import catLyingOnBack from '@/customer/assets/cat-lying-on-back.svg'
import { StackedHeadline, type StackedHeadlineColor } from '@/customer/components/stacked-headline'
import { buttonVariants } from '@/customer/components/ui/button'
import { useDocumentTitle } from '@/customer/hooks/use-document-title'
import { routes } from '@/customer/routes'

import { cn } from '../lib/utils'

interface NotFoundProps {
  /**
   * Stacked headline lines. Each line is rendered with a cycling
   * brand underline so callers just pass the words. Defaults to a
   * generic "well, this is awkward" for unmatched routes.
   */
  headline?: string[]
  /** Body copy under the headline. */
  message?: string
  /** Document title while this is mounted. */
  documentTitle?: string
  /** Optional content between the message and navigation action. */
  accessory?: ReactNode
  /**
   * "Get me out of here" CTA under the message. Defaults to the
   * landing route, which works for both signed-in (redirected to
   * `/account`) and signed-out visitors.
   */
  backLink?: { label: string; to: string }
}

/**
 * The cycle DESIGN.md prescribes for stacked headlines - each line
 * picks the next colour so adjacent lines never repeat.
 */
const HEADLINE_COLOR_CYCLE: StackedHeadlineColor[] = ['yellow', 'orange', 'blue', 'pink', 'green']

/**
 * Full-page "not found" state. Rendered as the catch-all route and
 * also inline by scoped guards (e.g. `<PetScope />`) when a URL
 * param doesn't resolve to a real entity. Overridable copy + back
 * link lets those callers frame the miss in their own terms.
 */
export function NotFound({
  headline = ['Well, this', 'is awkward'],
  message = "We couldn't find that page.",
  documentTitle = 'Page not found',
  accessory,
  backLink = { label: 'Go home', to: routes.root },
}: NotFoundProps = {}) {
  useDocumentTitle(documentTitle)

  const lines = headline.map((text, i) => ({
    text,
    color: HEADLINE_COLOR_CYCLE[i % HEADLINE_COLOR_CYCLE.length],
  }))

  return (
    <div className="flex flex-1 flex-col items-center justify-center gap-10 p-8 text-center">
      <img src={catLyingOnBack} alt="" aria-hidden="true" className="h-48 w-auto max-w-full" />

      <StackedHeadline align="center" lines={lines} />

      <p className="text-body-lg text-muted-foreground">{message}</p>

      {accessory}

      <Link to={backLink.to} className={cn(buttonVariants(), 'px-12')}>
        {backLink.label}
      </Link>
    </div>
  )
}
